import autoCompleteJs from "@tarekraafat/autocomplete.js";
import {
  AutoCompleteOptions,
  AutoCompleteResultItem,
  AutoCompleteSelectionEvent,
} from "../types/autocomplete";
import { AutocompleteLocation, Location } from "../types/location.types";
import { FormatService } from "../utils/format.utils";
import "../styles/autocomplete.styles.css";

const SELECTOR_TEMPLATE =
  "template#xchange-public-leasing-search-bar__chip__template";
const SELECTOR_CHIPS_CONTAINER = ".xchange-public-leasing-search-bar__chips";
const SELECTOR_CHIPS_ITEM = ".xchange-public-leasing-search-bar__chips__chip";

export class AutoComplete {
  public el: HTMLDivElement;
  public input: HTMLInputElement;
  public inputUnlo: HTMLInputElement;
  public autoComplete!: autoCompleteJs;
  public options: AutoCompleteOptions;

  private source!: AutocompleteLocation[];
  private template: HTMLTemplateElement;
  private chipsContainer: HTMLDivElement;
  private chips: Map<string, string>;
  private formatService: FormatService;

  constructor(el: HTMLDivElement, options: AutoCompleteOptions) {
    this.el = el;
    this.input = this.el.querySelector(".autocomplete-input")!;
    this.inputUnlo = this.el.querySelector(".autocomplete-unlo")!;
    this.options = options;
    this.template = document.querySelector(SELECTOR_TEMPLATE)!;
    this.chipsContainer = this.el.querySelector(SELECTOR_CHIPS_CONTAINER)!;
    this.chips = new Map();
    this.formatService = new FormatService();

    this.init();
  }

  init() {
    this.formatLocations();
    this.initAutoComplete();
    this.updateChipContainerWidth();

    window.addEventListener("resize", () => this.updateChipContainerWidth());
    this.input.addEventListener("keydown", (event: KeyboardEvent) => {
      const { key } = event;
      const { query } = this.autoComplete.feedback;

      /**
       * FIXME: Fix the following bug:
       * - enter a location
       * - immediately after chip has been added, press backspace
       * - SHOULD: remove recently added chip
       * - IS: nothing happens
       * - NOTE: probably related to events.input.selection further down
       */

      // Remove last chip when pressing backspace on empty input
      if (key === "Backspace" && query.length === 0) {
        this.removeLastChip();
      }
    });
  }

  /**
   * Format unsorted locations array to an array with regions / countries as keys
   */
  formatLocations() {
    const locations: Location[] = this.options.src;
    const regions = this.formatService.deriveRegions(locations);
    const regionObject = this.formatService.sortCountriesToRegions(
      regions,
      locations
    );

    const sortedArray = this.formatService.convertObjectToArray(regionObject);

    this.source = sortedArray;
  }

  initAutoComplete() {
    this.autoComplete = new autoCompleteJs<AutocompleteLocation>({
      selector: () => this.input,
      data: {
        src: this.source,
        keys: this.options.keys,
        cache: true,
        filter: (list) => {
          // Create Array from Set of all found items' unlocodes
          const unlocodeArray = Array.from(
            new Set(list.map((item) => item.value.unlocode))
          );
          // Filter duplicates of found items
          const filteredResults = unlocodeArray.map((unlocode) =>
            list.find((item) => item.value.unlocode === unlocode)
          );

          return filteredResults;
        },
      },
      placeHolder: this.options.placeHolder,
      resultsList: {
        maxResults: 100,
        // Automatically select the first result when typing
        element: () => this.autoComplete.goTo(0),
      },
      resultItem: {
        highlight: true,
        element: (item: HTMLElement, data: AutoCompleteResultItem) => {
          item.innerHTML = `<span>${data.value.label}</span>`;
        },
      },
      trigger: () => {
        return true;
      },
      events: {
        input: {
          focus: () => {
            this.autoComplete.start();
          },
          selection: (event: CustomEvent<AutoCompleteSelectionEvent>) => {
            this.addChip(event.detail.selection.value);
            this.autoComplete.input.value = "";
          },
        },
      },
    });
  }

  addChipByUnlocode(unlocode: string) {
    const locationInfo = this.source.find(
      (location) => location.unlocode === unlocode
    );

    if (!locationInfo) {
      return;
    }

    this.addChip(locationInfo);
  }

  addChip(locationInfo: AutocompleteLocation) {
    if (this.chips.has(locationInfo.unlocode)) {
      return;
    }

    if (this.chips.size >= 1) {
      this.removeLastChip();
    }

    // Clone template
    const clone = this.template.content.cloneNode(true) as HTMLElement;
    const chipEl: HTMLDivElement = clone.querySelector(SELECTOR_CHIPS_ITEM)!;

    // Update label
    const label = clone.querySelector("span")!;
    label.textContent = locationInfo.chipLabel || locationInfo.label;

    // Add event listener to remove Chip
    const closeBtn = clone.querySelector("svg")!;
    closeBtn.addEventListener("click", () =>
      this.removeChip(chipEl, locationInfo.unlocode)
    );

    // Add data attributes to receive values on form submit
    chipEl.setAttribute("data-value", locationInfo.unlocode);
    this.inputUnlo.value = locationInfo.unlocode;

    // Add chip to map to keep track of all selected values
    this.chips.set(
      locationInfo.unlocode,
      locationInfo.chipLabel || locationInfo.label
    );

    this.chipsContainer.append(clone);
    this.updateChipContainerWidth();
    this.checkChipAmount();
  }

  removeChip(
    target: HTMLDivElement,
    unlocode: AutocompleteLocation["unlocode"]
  ) {
    // Remove from DOM
    target.remove();

    // Remove from Map
    this.chips.delete(unlocode);

    this.inputUnlo.value = "";

    this.updateChipContainerWidth();
    this.checkChipAmount();
  }

  removeLastChip() {
    if (this.chips.size === 0) {
      return;
    }

    const lastChip = Array.from([...this.chips]).pop();

    if (!lastChip) {
      return;
    }

    const [unlocode] = lastChip;

    const chipEl: HTMLDivElement | null = this.chipsContainer.querySelector(
      `[data-value="${unlocode}"]`
    );

    if (!chipEl) {
      return;
    }

    this.removeChip(chipEl, unlocode);
  }

  updateChipContainerWidth() {
    const { width } = this.chipsContainer.getBoundingClientRect();
    const padding = Math.ceil(width) > 40 ? `${Math.ceil(width)}px` : "0px";

    this.el.style.setProperty("--padding", padding);
  }

  getSelectedLocations(): Location["unlocode"][] {
    const selectedLocations: Location["unlocode"][] = [...this.chips.keys()];

    return selectedLocations;
  }

  public checkChipAmount() {
    this.el
      .querySelector(".autoComplete_wrapper")!
      .classList.toggle("-dirty", this.chips.size > 0);
  }

  public select(unlocodes: string[]) {
    /**
     * Check for Regions first. If a match is found,
     * display the region as a single chip.
     */
    const regionValue = this.source.find(
      (location) => location.unlocode === unlocodes.join(",")
    );

    if (regionValue) {
      this.addChip(regionValue);
      return;
    }

    /**
     * If no region was matched, display each unlocode as a separate chip
     */
    unlocodes.forEach((unlocode) => {
      const locationValue = this.source.find(
        (location) => location.unlocode === unlocode
      );

      if (locationValue) {
        this.addChip(locationValue);
      }
    });
  }
}
